import { ElementRef, useRef } from "react";

import { unstable_useControl as useControl, useField } from "@conform-to/react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { RadioGroup as ShadRadioGroup, RadioGroupItem as ShadRadioGroupItem } from "~/components/ui/radio-group";
import { cn } from "~/lib/utils";

export function RadioGroup({
    name,
    onValueChange,
    ...props
}: { name: string } & React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>) {
    const radioGroupRef = useRef<ElementRef<typeof RadioGroup> | null>(null);
    const [meta] = useField(name);
    const control = useControl(meta);
    return (
        <>
            <input
                ref={control.register}
                name={meta.name}
                defaultValue={meta.initialValue}
                tabIndex={-1}
                className="sr-only"
                aria-hidden
                onFocus={() => {
                    if (radioGroupRef.current) {
                        radioGroupRef.current?.focus();
                    }
                }}
            />
            <ShadRadioGroup
                ref={radioGroupRef}
                className="flex items-stretch gap-4"
                value={control.value ?? ""}
                onValueChange={(value) => {
                    control.change(value);
                    if (onValueChange) {
                        onValueChange(value);
                    }
                }}
                onBlur={control.blur}
                {...props}
            />
        </>
    );
}

export function RadioGroupItem(props: React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>) {
    return <ShadRadioGroupItem {...props} />;
}

export function RadioWithContentGroupItem({
    title,
    description,
    value,
    className,
    variant = "default",
    ...props
}: {
    title: JSX.Element | string;
    description?: JSX.Element | string;
    value: string;
    className?: string;
    variant?: "default" | "content";
}) {
    return (
        <label
            className={cn(
                "has-state-checked",
                "border-black border-opacity-30 border-2 rounded-md p-4 cursor-pointer flex flex-col gap-4",
                "hover:border-opacity-60",
                className,
                variant === "default" && "w-full gap-16 text-foreground/40",
            )}
        >
            <div className="flex justify-between items-center gap-2">
                <p className="text-2xl font-semibold">{title}</p>
                <RadioGroupItem value={value} {...props} />
            </div>
            {description !== undefined ? <div className="text-black grow">{description}</div> : null}
        </label>
    );
}
